/**
 * TechnicianTrainingVerificationCard
 *
 * Manager-facing surface inside TechnicianEditor that hosts the
 * verification + revocation actions per the 18-E2 / 18-E8 / 18-E10
 * roadmaps. Single Card with two sections:
 *
 *   1. Pending Verification (18-E12 original) — sessions the
 *      technician has manually marked complete and a manager has not
 *      yet signed off. Eligibility filter:
 *        completion_status IN ('completed', 'partial')
 *        AND manual_marked_complete_at IS NOT NULL
 *        AND manager_verified_at IS NULL
 *      Each row hosts a VerifyTrainingButton.
 *
 *   2. Manager-Verified Training (18-E17 addition) — sessions
 *      currently carrying a manager signature. Filter:
 *        manager_verified_at IS NOT NULL
 *      Each row hosts a RevokeTrainingButton. Section is hidden when
 *      there are no verified sessions.
 *
 * Feature flag: managerTrainingVerification. When disabled, the card
 * returns null and neither query runs.
 *
 * This component does NOT:
 *   - write to training_consumption_sessions directly
 *   - write to training_completion_verifications directly
 *   - emit training_effectiveness_events
 *   - render the manager queue or Passport badges
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, ShieldCheck, ListChecks } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { VerifyTrainingButton } from './VerifyTrainingButton';
import { RevokeTrainingButton } from './RevokeTrainingButton';
import { TrainingEvidenceDrawer } from './TrainingEvidenceDrawer';
import { getCompletionProvenanceLabel } from '@/utils/training/evidenceProvenanceLabels';
import {
  buildManagerRevocationRow,
  buildManagerVerificationRow,
  buildManualCompletionRow,
  summarizeTrainingEvidenceRows,
  type TrainingEvidenceDisplayRow,
} from '@/utils/training/evidenceDisplayRows';

interface PendingVerificationRow {
  id: string;
  completion_status: string | null;
  manual_marked_complete_at: string | null;
  manager_verified_at: string | null;
  confirmed_at: string | null;
  actual_time_spent_minutes: number | null;
  artifact: {
    id: string;
    title: string;
    provider_name: string | null;
  } | null;
}

interface VerifiedSessionRow {
  id: string;
  completion_status: string | null;
  completion_confidence: string | null;
  manager_verified_at: string | null;
  manager_verification_note: string | null;
  artifact: {
    id: string;
    title: string;
    provider_name: string | null;
  } | null;
}

// Batch 18-F15.3: revoked-verification audit row shape, mirrors the
// canonical 18-E18 / 18-F1 query in LearningImpactTab. The audit
// table has no FK to training_artifacts (artifact_id is denormalized),
// so the drawer's manager_revocation rows fall back to the F15.2
// "Training verification" detail rather than threading a deep join.
// metadata carries the auto_revoked / one_shot_reconciliation flags
// the F15.2 buildManagerRevocationRow helper already understands.
interface RevokedVerificationRow {
  id: string;
  session_id: string;
  verification_status: string;
  verification_note: string | null;
  created_at: string;
  artifact_id: string;
  metadata: {
    auto_revoked?: boolean;
    one_shot_reconciliation?: boolean;
    [key: string]: unknown;
  } | null;
}

interface TechnicianTrainingVerificationCardProps {
  technicianProfileId: string | null;
  technicianName?: string;
}

export function TechnicianTrainingVerificationCard({
  technicianProfileId,
  technicianName,
}: TechnicianTrainingVerificationCardProps) {
  const flagEnabled = useFeatureFlag('managerTrainingVerification');
  const queryClient = useQueryClient();

  const { data: rows, isLoading } = useQuery({
    queryKey: ['pending-manager-verifications', technicianProfileId],
    queryFn: async (): Promise<PendingVerificationRow[]> => {
      if (!technicianProfileId) return [];
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(
          'id, completion_status, manual_marked_complete_at, manager_verified_at, confirmed_at, actual_time_spent_minutes, artifact:training_artifacts(id, title, provider_name)',
        )
        .eq('technician_profile_id', technicianProfileId)
        .in('completion_status', ['completed', 'partial'])
        .not('manual_marked_complete_at', 'is', null)
        .is('manager_verified_at', null)
        .order('manual_marked_complete_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PendingVerificationRow[];
    },
    enabled: !!technicianProfileId && flagEnabled,
  });

  // Batch 18-E17: read-only verified-sessions query for the revoke
  // section. Same access pattern as the Passport visibility query
  // (18-E13) — single inline useQuery, no new broad data architecture.
  const { data: verifiedRows } = useQuery({
    queryKey: ['verified-manager-verifications', technicianProfileId],
    queryFn: async (): Promise<VerifiedSessionRow[]> => {
      if (!technicianProfileId) return [];
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(
          'id, completion_status, completion_confidence, manager_verified_at, manager_verification_note, artifact:training_artifacts(id, title, provider_name)',
        )
        .eq('technician_profile_id', technicianProfileId)
        .not('manager_verified_at', 'is', null)
        .order('manager_verified_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as VerifiedSessionRow[];
    },
    enabled: !!technicianProfileId && flagEnabled,
  });

  // Batch 18-F15.3: read-only revoked-verification audit query for
  // the manager Training Evidence Drawer. Mirrors the canonical
  // 18-E18 / 18-F1 LearningImpactTab query but uses a manager-side
  // queryKey so it doesn't collide with the technician Passport
  // cache. RLS policy "Managers/admins view shop verifications"
  // already grants the manager-feature-flagged surface SELECT
  // access — no new policy / RPC / migration. The query is gated on
  // the same `managerTrainingVerification` flag as the rest of the
  // card so disabling the flag skips the network entirely.
  //
  // The drawer never mutates these rows; this card never writes to
  // training_completion_verifications. The audit-table append is
  // owned by the E11/E15 atomic RPCs (verify + revoke).
  const { data: revokedRows } = useQuery({
    queryKey: ['manager-revoked-verifications', technicianProfileId],
    queryFn: async (): Promise<RevokedVerificationRow[]> => {
      if (!technicianProfileId) return [];
      const { data, error } = await supabase
        .from('training_completion_verifications')
        .select(
          'id, session_id, verification_status, verification_note, created_at, artifact_id, metadata',
        )
        .eq('technician_profile_id', technicianProfileId)
        .eq('verification_status', 'revoked')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as RevokedVerificationRow[];
    },
    enabled: !!technicianProfileId && flagEnabled,
  });

  // Batch 18-F15.4: hydrate artifact titles for the drawer's
  // manager_revocation rows. The audit table has no FK to
  // training_artifacts (artifact_id is denormalized), so this
  // mirrors the canonical 18-E19a Passport pattern: collect unique
  // artifact_ids, query training_artifacts by id IN (...) selecting
  // only the privacy-minimum columns (id + title — no provider_name,
  // no source URL, no metadata), then build a Map<id, title> the
  // useMemo below reads from. Hydration is OPTIONAL: when a title is
  // not found, the row falls back to the F15.2 helper default
  // ("Training verification" detail).
  const revokedArtifactIds = useMemo(() => {
    if (!revokedRows) return [] as string[];
    const ids = new Set<string>();
    for (const row of revokedRows) {
      if (row.artifact_id) ids.add(row.artifact_id);
    }
    return Array.from(ids);
  }, [revokedRows]);

  const { data: revokedArtifactRows } = useQuery({
    queryKey: [
      'manager-revoked-artifact-titles',
      technicianProfileId,
      revokedArtifactIds,
    ],
    queryFn: async (): Promise<Array<{ id: string; title: string }>> => {
      if (revokedArtifactIds.length === 0) return [];
      const { data, error } = await supabase
        .from('training_artifacts')
        .select('id, title')
        .in('id', revokedArtifactIds);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; title: string }>;
    },
    enabled:
      !!technicianProfileId &&
      flagEnabled &&
      !!revokedRows &&
      revokedArtifactIds.length > 0,
  });

  const revokedArtifactTitleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of revokedArtifactRows ?? []) {
      if (a && typeof a.title === 'string' && a.title.length > 0) {
        map.set(a.id, a.title);
      }
    }
    return map;
  }, [revokedArtifactRows]);

  // Batch 18-F15.2: manager-only Training Evidence Drawer state.
  // Sheet visibility is local component state; the drawer reads only
  // the typed rows array assembled below from data already in scope.
  const [evidenceDrawerOpen, setEvidenceDrawerOpen] = useState(false);

  // Batch 18-F25: focus return on drawer close. The View evidence
  // trigger is a plain <Button>, NOT a <SheetTrigger>, so Radix has
  // no way to auto-return focus to it when the Sheet closes via Esc /
  // X / outside-click. We capture a ref to the button and, on the
  // close transition only, schedule a focus() call on the next animation
  // frame so it lands after Radix has finished unmounting its overlay.
  // No custom keyboard wiring — Esc is still delivered by Radix Sheet
  // and Cmd/Ctrl+P is still delivered by the browser.
  const evidenceTriggerRef = useRef<HTMLButtonElement | null>(null);
  const handleEvidenceDrawerOpenChange = useCallback(
    (nextOpen: boolean) => {
      setEvidenceDrawerOpen(nextOpen);
      if (!nextOpen) {
        requestAnimationFrame(() => {
          evidenceTriggerRef.current?.focus();
        });
      }
    },
    [],
  );

  // Batch 18-F15.2: assemble manager-only evidence rows from the data
  // the parent already loaded. NO extension or provider evidence is
  // read here.
  //
  // Batch 18-F15.3: fan in manager_revocation rows from the new
  // revoked-verifications query above. Trust-class separation is
  // preserved because each row goes through its own typed F15.2
  // builder — the row's `kind` literal cannot drift.
  //
  // Computed BEFORE the early returns so the hook ordering stays
  // stable across renders.
  const evidenceRows: TrainingEvidenceDisplayRow[] = useMemo(() => {
    const all: TrainingEvidenceDisplayRow[] = [];
    for (const row of rows ?? []) {
      if (row.manual_marked_complete_at) {
        all.push(
          buildManualCompletionRow({
            id: `mc:${row.id}`,
            manualMarkedCompleteAt: row.manual_marked_complete_at,
            completionStatus: row.completion_status,
            artifactTitle: row.artifact?.title ?? null,
          }),
        );
      }
    }
    for (const row of verifiedRows ?? []) {
      if (row.manager_verified_at) {
        all.push(
          buildManagerVerificationRow({
            id: `mv:${row.id}`,
            managerVerifiedAt: row.manager_verified_at,
            managerVerificationNote: row.manager_verification_note,
            artifactTitle: row.artifact?.title ?? null,
          }),
        );
      }
    }
    for (const row of revokedRows ?? []) {
      all.push(
        buildManagerRevocationRow({
          id: `mr:${row.id}`,
          revokedAt: row.created_at,
          revocationNote: row.verification_note,
          autoRevoked: !!row.metadata?.auto_revoked,
          oneShotReconciliation: !!row.metadata?.one_shot_reconciliation,
          // Batch 18-F15.4: hydrated title from the batched
          // training_artifacts lookup above. When the lookup misses
          // (artifact deleted, RLS hides the row), fall back to null
          // and let the F15.2 helper render the default detail.
          artifactTitle:
            revokedArtifactTitleById.get(row.artifact_id) ?? null,
        }),
      );
    }
    return all;
  }, [rows, verifiedRows, revokedRows, revokedArtifactTitleById]);

  // Batch 18-F17: surface the evidence count beside the "View
  // evidence" trigger so managers can see at-a-glance how much
  // evidence the drawer would render without having to open it.
  // Pure derivation from evidenceRows via the F16 helper — no new
  // query, no new mutation. Always rendered (including when total
  // is zero) so the empty state is visible too.
  const evidenceSummary = useMemo(
    () => summarizeTrainingEvidenceRows(evidenceRows),
    [evidenceRows],
  );

  if (!flagEnabled) return null;
  if (!technicianProfileId) return null;

  const pending = rows ?? [];
  const verified = verifiedRows ?? [];

  const handleVerified = () => {
    queryClient.invalidateQueries({
      queryKey: ['pending-manager-verifications', technicianProfileId],
    });
    queryClient.invalidateQueries({
      queryKey: ['verified-manager-verifications', technicianProfileId],
    });
    queryClient.invalidateQueries({ queryKey: ['consumption-sessions'] });
  };

  // Batch 18-E17: on successful revoke the session pointer columns
  // are cleared, so the row should move from the verified list back
  // to pending (if still in completed/partial state). Invalidate
  // both lists + the Passport-side query consumed by 18-E13.
  const handleRevoked = () => {
    queryClient.invalidateQueries({
      queryKey: ['pending-manager-verifications', technicianProfileId],
    });
    queryClient.invalidateQueries({
      queryKey: ['verified-manager-verifications', technicianProfileId],
    });
    queryClient.invalidateQueries({
      queryKey: ['passport-verified-sessions', technicianProfileId],
    });
    // Batch 18-F15.3: refresh the drawer's revocation list so a
    // freshly-revoked row appears immediately. The Passport-side
    // revoked-verifications key is invalidated separately by 18-E18's
    // own invalidation (LearningImpactTab owns that cache).
    queryClient.invalidateQueries({
      queryKey: ['manager-revoked-verifications', technicianProfileId],
    });
    queryClient.invalidateQueries({ queryKey: ['consumption-sessions'] });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Training Verification
          {pending.length > 0 && (
            <Badge variant="secondary" className="ml-1">
              {pending.length} pending
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Confirm training sessions the technician has marked complete.
        </CardDescription>
        {/* Batch 18-F15.2 — manager-only Training Evidence Drawer
            trigger. Disabled when there is nothing to show so we never
            open an empty Sheet.
            Batch 18-F17 — adds a sibling count Badge so managers can
            see the evidence record count without opening the drawer.
            The Badge is a presentational <div>, NOT a button/link —
            the trigger remains the only interactive element. */}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Badge
            variant="secondary"
            className="text-[11px]"
            aria-label={`Training evidence: ${evidenceSummary.total} ${
              evidenceSummary.total === 1 ? 'record' : 'records'
            } available for review · ${
              evidenceSummary.manualCompletion
            } manual, ${
              evidenceSummary.managerVerification
            } verified, ${evidenceSummary.managerRevocation} revoked`}
            data-testid="training-evidence-count-badge"
          >
            Training evidence: {evidenceSummary.total}{' '}
            {evidenceSummary.total === 1 ? 'record' : 'records'}
          </Badge>
          <Button
            ref={evidenceTriggerRef}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setEvidenceDrawerOpen(true)}
            disabled={evidenceRows.length === 0}
            data-testid="training-evidence-drawer-trigger"
          >
            <ListChecks className="h-4 w-4 mr-2" />
            View evidence
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pending Verification section (18-E12 original) */}
        <div className="space-y-2">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {!isLoading && pending.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No sessions awaiting verification.
            </p>
          )}
          {pending.map((row) => (
            <div
              key={row.id}
              className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-card"
              data-testid="pending-verification-row"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {row.artifact?.title ?? 'Training session'}
                </p>
                {row.artifact?.provider_name && (
                  <p className="text-xs text-muted-foreground">
                    {row.artifact.provider_name}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="text-xs">
                    {row.completion_status === 'partial' ? 'Partial' : 'Completed'}
                  </Badge>
                  {row.actual_time_spent_minutes != null && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {row.actual_time_spent_minutes} min
                    </span>
                  )}
                </div>
                {/* Batch 18-F15.2 — self-attested provenance line. Names
                    the trust class behind the row (the technician's own
                    manual mark) so the manager reviewing this queue
                    cannot confuse it with manager-verified or
                    provider-attested completion. */}
                {row.manual_marked_complete_at && (
                  <p
                    className="mt-1 text-[10px] text-muted-foreground/80 uppercase tracking-wide"
                    data-testid="pending-verification-provenance"
                  >
                    {getCompletionProvenanceLabel('manual')}
                  </p>
                )}
              </div>
              <div className="shrink-0">
                <VerifyTrainingButton
                  sessionId={row.id}
                  completionStatus={row.completion_status}
                  manualMarkedCompleteAt={row.manual_marked_complete_at}
                  managerVerifiedAt={row.manager_verified_at}
                  artifactTitle={row.artifact?.title}
                  technicianName={technicianName}
                  onVerified={handleVerified}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Manager-Verified Training section (18-E17 addition).
            Hidden when there are no verified sessions. */}
        {verified.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Manager-Verified Training
            </p>
            {verified.map((row) => (
              <div
                key={row.id}
                className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-muted/20"
                data-testid="verified-session-row"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {row.artifact?.title ?? 'Training session'}
                  </p>
                  {row.artifact?.provider_name && (
                    <p className="text-xs text-muted-foreground">
                      {row.artifact.provider_name}
                    </p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {row.completion_confidence && (
                      <Badge variant="outline" className="text-xs">
                        {row.completion_confidence}
                      </Badge>
                    )}
                  </div>
                  {row.manager_verification_note && (
                    <p className="mt-1 text-xs text-muted-foreground italic line-clamp-2">
                      “{row.manager_verification_note}”
                    </p>
                  )}
                </div>
                <div className="shrink-0">
                  <RevokeTrainingButton
                    sessionId={row.id}
                    managerVerifiedAt={row.manager_verified_at}
                    completionStatus={row.completion_status}
                    artifactTitle={row.artifact?.title}
                    onRevoked={handleRevoked}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      {/* Batch 18-F15.2 — manager-only Training Evidence Drawer. Reads
          only the typed rows assembled above from data already in
          scope; never issues a query or mutation. */}
      <TrainingEvidenceDrawer
        open={evidenceDrawerOpen}
        onOpenChange={handleEvidenceDrawerOpenChange}
        technicianName={technicianName}
        rows={evidenceRows}
      />
    </Card>
  );
}
